import { isConnectionsV2Enabled } from '@/config/connectionsV2';
import { isInviteEmailMismatch } from '@/helpers/inviteCode.js';
import { buildSellerOnlyMarketplaces, isSelectableAdsProfile } from '@/helpers/marketplaceUnion.js';
import ability from '@/plugins/casl/ability';
import dayjs from 'dayjs';

/**
 * What a signed-in user opening an auth page with ?inviteCode gets. `pendingMismatch` is true when
 * bootstrap already found the invite addressed to another email.
 *  - register, invite for another email: 'stay' (the page asks before anything is signed out)
 *  - register, same or unknown email: 'redirect' to the landing route. Bootstrap already accepted
 *    the invite, so signing the user out would undo it.
 *  - login / forgot-password: 'signOut' (staying signed in would mix two identities).
 */
export const resolveInviteLinkWhileSignedIn = (to, { userEmail, pendingMismatch = false } = {}) => {
  if (to.name !== 'register') return { action: 'signOut' };
  if (pendingMismatch || isInviteEmailMismatch(userEmail, to.query.email)) {
    return { action: 'stay' };
  }
  const query = { ...to.query };
  delete query.inviteCode;
  delete query.email;
  return { action: 'redirect', to: { path: '/', query } };
};

/**
 * Returns the home route for the currently authenticated user based on their CASL abilities.
 * This is the single source of truth for post-login and "/" redirects.
 * Add new role conditions here if roles are expanded in the future.
 */
export const getHomeRoute = () => {
  if (ability.can('read', 'User')) return { name: 'dashboard' };
  return { name: 'login' };
};

// Where a Seller-Central-only business profile lands: the dashboard is Ads data, so it would
// open on an empty "connect Amazon Ads" state. Business Reports is the seller data.
export const SELLER_ONLY_LANDING_ROUTE_NAME = 'business-reports-sales-traffic-by-asin-dk';

/**
 * The landing route for a business profile that has Seller Central but no Amazon Ads profiles —
 * Business Reports on its first seller-only marketplace — or null for any other profile (or
 * when the user may not read User pages). `accountStore` is the account store.
 */
export const getSellerOnlyLandingRoute = (accountStore) => {
  if (!ability.can('read', 'User')) return null;
  const current = accountStore?.currentBusinessProfile;
  const businessProfileId = current?.id;
  if (!businessProfileId) return null;
  const profile =
    (accountStore.businessProfiles || []).find((p) => p.id === businessProfileId) || current;
  // Same predicate as the marketplace dropdown: attribution / agency Ads profiles don't count.
  const hasAds = (profile.amazonAdsAccounts || []).some((acc) =>
    (acc.amazonAdsProfiles || []).some(isSelectableAdsProfile),
  );
  if (hasAds) return null;
  const [firstSellerMarketplace] = buildSellerOnlyMarketplaces(profile);
  if (!firstSellerMarketplace) return null;
  return {
    name: SELLER_ONLY_LANDING_ROUTE_NAME,
    query: { businessProfileId, marketplace: firstSellerMarketplace.marketplaceUid },
  };
};

// Where a business profile whose only connection is eBay lands: the Amazon dashboard would open
// on an empty "connect Amazon Ads" state.
export const EBAY_LANDING_ROUTE_NAME = 'ebay-overview';

// The business profile's Amazon connections, from the account store's payload (no request).
const amazonConnectionsOf = (accountStore) => {
  const current = accountStore?.currentBusinessProfile;
  const businessProfileId = current?.id;
  if (!businessProfileId) return null;
  const profile =
    (accountStore.businessProfiles || []).find((p) => p.id === businessProfileId) || current;
  const hasAds = (profile.amazonAdsAccounts || []).some((acc) =>
    (acc.amazonAdsProfiles || []).some(isSelectableAdsProfile),
  );
  const hasSeller = (profile.amazonSPAccounts || []).length > 0;
  return { businessProfileId, hasAmazon: hasAds || hasSeller };
};

// The one eBay lookup behind both landings. `lookup` is the caller's permission to fetch (the eBay
// pages gate, or an onboarding decision). `state` is 'skipped' (lookup not wanted, the user may not
// read User pages, no business profile, or the profile has Amazon: nothing was fetched),
// 'active', 'none', or 'failed' (the request threw: unknown, never read as "nothing connected").
const lookUpEbayOnly = async (accountStore, { lookup }) => {
  const amazon = amazonConnectionsOf(accountStore);
  if (!lookup || !ability.can('read', 'User') || !amazon || amazon.hasAmazon) {
    return { amazon, state: 'skipped' };
  }
  try {
    const connections = await accountStore.fetchEbayConnections({
      businessProfileId: amazon.businessProfileId,
    });
    const active = (connections || []).some((c) => c?.status === 'ACTIVE');
    return { amazon, state: active ? 'active' : 'none' };
  } catch {
    return { amazon, state: 'failed' };
  }
};

// A business profile counts as new for this many days after the manager created it.
const NEW_PROFILE_DAYS = 14;

// Whether the current business profile was created within NEW_PROFILE_DAYS. `createdAt` is the
// manager's ISO UTC timestamp; a missing or unparseable one means NOT new.
const isNewBusinessProfile = (accountStore) => {
  const current = accountStore?.currentBusinessProfile;
  if (!current?.id) return false;
  const profile = (accountStore.businessProfiles || []).find((p) => p.id === current.id) || current;
  const raw = profile.createdAt ?? current.createdAt;
  if (!raw) return false;
  const createdAt = dayjs(raw);
  if (!createdAt.isValid()) return false;
  return dayjs().diff(createdAt, 'day', true) <= NEW_PROFILE_DAYS;
};

const ebayLandingRoute = (businessProfileId) => ({
  name: EBAY_LANDING_ROUTE_NAME,
  query: { businessProfileId },
});

/**
 * The eBay home for a business profile whose only connection is an ACTIVE eBay account (no
 * selectable Ads profile, no Seller Central account), or null for any other profile. The account
 * store's profile payload carries no eBay data, so this asks the eBay connections endpoint, and
 * only for a profile with no Amazon connection at all. `ebayAllowed` is the dev-only gate
 * (isEbayPagesAllowed): when it is false nothing is fetched and the caller keeps its usual landing,
 * so a user is never sent to a route the guard blocks. A failed lookup also returns null.
 */
export const getEbayOnlyLandingRoute = async (accountStore, { ebayAllowed = false } = {}) => {
  const { amazon, state } = await lookUpEbayOnly(accountStore, { lookup: ebayAllowed });
  return state === 'active' ? ebayLandingRoute(amazon.businessProfileId) : null;
};

// Onboarding page (connect-accounts). Every stage there can be skipped.
export const ONBOARDING_ROUTE_NAME = 'connect-accounts';

/**
 * Where to send the user after onboarding / sign-in: the seller-only landing (see
 * getSellerOnlyLandingRoute) when it applies, the eBay home for an eBay-only profile (see
 * getEbayOnlyLandingRoute; `ebayAllowed` opens that branch), otherwise getHomeRoute() with the
 * current business profile and marketplace passed explicitly, so the destination renders with
 * unambiguous context (same as a hard reload).
 *
 * `onboardIfEmpty` is for sign-in callers only, never the onboarding page itself (its "Connect
 * later" would loop back). A profile with no connection at all (no selectable Ads profile, no
 * Seller Central account and no ACTIVE eBay account) then lands on the connect-accounts page
 * instead of an empty dashboard, but only while the profile is new (created within the last 14
 * days, from the manager's `createdAt`; a missing or unparseable one counts as not new). Older
 * empty profiles keep the usual landing and skip the eBay lookup. Owners / admins only: an invited
 * plain user cannot connect anything. For this decision the eBay lookup runs regardless of the `ebayAllowed` gate (an
 * eBay-only customer is not "empty" on prod, where eBay pages stay gated); the eBay landing itself
 * stays gated. A failed lookup keeps the usual landing. Shopify / Walmart / Meta / Google
 * connections are not checked (no store holds them at sign-in). With Connections v2 on, that
 * empty-profile landing is the Connections page (`settings-connections`) instead.
 */
export const getLandingRoute = async (
  accountStore,
  { ebayAllowed = false, onboardIfEmpty = false } = {},
) => {
  const sellerOnly = getSellerOnlyLandingRoute(accountStore);
  if (sellerOnly) return sellerOnly;
  const canOnboard =
    onboardIfEmpty && ability.can('read', 'Management') && isNewBusinessProfile(accountStore);
  const { amazon, state } = await lookUpEbayOnly(accountStore, {
    lookup: ebayAllowed || canOnboard,
  });
  if (state === 'active' && ebayAllowed) return ebayLandingRoute(amazon.businessProfileId);
  const businessProfileId = accountStore?.currentBusinessProfile?.id;
  if (
    canOnboard
    && amazon
    && !amazon.hasAmazon
    && state === 'none'
  ) {
    // Connections v2 replaces the onboarding wizard: an empty new profile lands on Connections.
    return {
      name: isConnectionsV2Enabled() ? 'settings-connections' : ONBOARDING_ROUTE_NAME,
      query: { businessProfileId },
    };
  }
  const query = {};
  const profileId = accountStore?.currentMarketplace?.profileId;
  if (businessProfileId) query.businessProfileId = businessProfileId;
  if (profileId) query.profileId = profileId;
  return { ...getHomeRoute(), query: Object.keys(query).length ? query : undefined };
};

// Business-profile / marketplace selection keys (see authFlowLocation): a landing sets its own
// explicitly, so a carried query never overrides them.
const LANDING_SELECTION_KEYS = ['businessProfileId', 'profileId', 'marketplace'];

// `landing` with `carryQuery` (e.g. tracking params) merged under its own query. The selection
// keys of `carryQuery` are dropped: the landing's resolved ones win.
export const withCarriedQuery = (landing, carryQuery = {}) => {
  const carried = { ...carryQuery };
  LANDING_SELECTION_KEYS.forEach((key) => delete carried[key]);
  const query = { ...carried, ...landing.query };
  return { ...landing, query: Object.keys(query).length ? query : undefined };
};

/**
 * The "you are signed in, where to?" decision for an entry that is not a fresh login form submit:
 * `/` with a live session and a sign-in completed in another tab. Same landing as sign-in
 * (getLandingRoute), but never `onboardIfEmpty`: an existing user opening the app must not be
 * pushed to /connect-accounts. Loads the business profiles first when the account store has
 * none yet (a cold page load: the store's own initial sync only starts after the first
 * navigation resolves), reusing a sync already in flight. Never throws: any failure, or a store
 * that still has no business profile, keeps the plain home route with `carryQuery` as before.
 */
export const resolveSignedInLanding = async (
  accountStore,
  { ebayAllowed = false, carryQuery = {} } = {},
) => {
  const fallback = {
    ...getHomeRoute(),
    query: Object.keys(carryQuery).length ? carryQuery : undefined,
  };
  try {
    if (!accountStore.currentBusinessProfile) await accountStore.syncBusinessProfiles();
    const landing = await getLandingRoute(accountStore, { ebayAllowed });
    // No resolved business profile: nothing was decided, keep the URL's own selection. Same for a
    // user who may not read User pages (the home route is login): no selection belongs on it.
    if (landing.name === 'login' || !landing.query?.businessProfileId) return fallback;
    return withCarriedQuery(landing, carryQuery);
  } catch (error) {
    console.error('[router] sign-in landing failed, using the home route:', error);
    return fallback;
  }
};

// Query keys a connect flow returns with. An OAuth session started before Connections v2 shipped
// still returns to /connect-accounts, so the redirect below must keep honouring them.
const AMAZON_RETURN_KEYS = ['sessionId', 'sp_error', 'code', 'error'];
const EBAY_RETURN_KEYS = ['ebayClaim', 'ebayError'];
// SP returns /integrations/sp/connected already handles (claim session, already-linked notice, legacy account id).
const SP_RETURN_KEYS = ['sp_session', 'sp_notice', 'account_id'];

const hasAnyKey = (query, keys) => keys.some((key) => query[key] !== undefined);

/**
 * Where the retired /connect-accounts wizard sends a visitor with Connections v2 on (a pure
 * decision; the router guard applies it): an Amazon sign-in return to the Amazon page, an SP claim
 * return to its landing, an eBay return to the eBay page, otherwise the Connections overview. The
 * whole query is forwarded, so no return parameter is lost. A user without Management cannot open
 * Connections and goes home. The manager's connect-reminder emails link here, so it never 404s.
 */
export const connectAccountsRedirect = (to, { canManage = false } = {}) => {
  if (!canManage) return getHomeRoute();
  const query = { ...to.query };
  if (hasAnyKey(query, SP_RETURN_KEYS)) return { path: '/integrations/sp/connected', query };
  if (hasAnyKey(query, EBAY_RETURN_KEYS)) return { path: '/settings/connections/ebay', query };
  if (hasAnyKey(query, AMAZON_RETURN_KEYS)) return { path: '/settings/connections/amazon', query };
  return { name: 'settings-connections', query };
};

/**
 * Where the retired dev-only Connections hub (/settings/connections-hub[/:provider]) sends a
 * visitor with Connections v2 on: the matching channel page, or the overview for the hub's index.
 * `to.params.provider` is the same channel key the v2 pages use. Query forwarded; non-Management
 * goes home.
 */
export const connectionsHubRedirect = (to, { canManage = false } = {}) => {
  if (!canManage) return getHomeRoute();
  const query = { ...to.query };
  const provider = String(to.params?.provider || '');
  if (!provider) return { name: 'settings-connections', query };
  return { path: `/settings/connections/${encodeURIComponent(provider)}`, query };
};

/**
 * The Overview (route `dashboard`) with the current business profile and, when one is selected,
 * the Ads profile in the query — the target of the app logos. Query-less until the account store
 * has hydrated, so the guard falls back to the stored selection.
 */
export const getOverviewRoute = (accountStore) => {
  const query = {};
  const businessProfileId = accountStore?.currentBusinessProfile?.id;
  const profileId = accountStore?.currentMarketplace?.profileId;
  if (businessProfileId) query.businessProfileId = businessProfileId;
  if (profileId) query.profileId = profileId;
  return { name: 'dashboard', query: Object.keys(query).length ? query : undefined };
};

// Accepts app-internal paths only. Rejects absolute URLs, protocol-relative
// ("//evil.com"), backslash tricks ("/\\evil.com") and schemes like javascript:.
// Used to guard post-login redirects driven by the ?to= query parameter.
export const isSafeInternalPath = (path) => {
  if (typeof path !== 'string' || path.length === 0) return false;
  if (!path.startsWith('/')) return false;
  const hasControlCharacter = [...path].some((char) => char <= '\u001f' || char === '\u007f');
  if (path.startsWith('//') || path.includes('\\') || hasControlCharacter) return false;
  // URL normalization can turn dot segments into a protocol-relative pathname.
  const url = new URL(path, 'http://localhost');
  return url.origin === 'http://localhost' && !url.pathname.startsWith('//');
};

export const sanitizeRedirectPath = (path) => (isSafeInternalPath(path) ? path : null);

// Business-profile / marketplace selection keys. A post-auth return drops them: the profile sync
// has already chosen the selection for the account that just signed in (which may not own the old
// one), and the guard strips them off the login URL for the same reason.
const SELECTION_QUERY_KEYS = ['businessProfileId', 'profileId', 'marketplace'];

// Carry the same return intent between login, registration and recovery. Keep it in the
// route, so two tabs can finish different flows. Never carry the old session's selection.
export const authFlowLocation = (name, query = {}, extraQuery = {}) => {
  const nextQuery = { ...query, ...extraQuery };
  SELECTION_QUERY_KEYS.forEach((key) => delete nextQuery[key]);
  const returnTo = sanitizeRedirectPath(nextQuery.to);
  if (returnTo) nextQuery.to = returnTo;
  else delete nextQuery.to;
  return { name, query: nextQuery };
};

// Turn a sanitized post-auth return path (`?to=` / postAuthReturnTo) into a router location.
// vue-router ignores a query or hash embedded in `path` — { path: '/a?b=1' } lands on '/a' — so
// they are split out here; otherwise every URL-only view setting on the target page is lost.
// `baseQuery` is merged under the target's own query.
export const returnPathToLocation = (path, baseQuery = {}) => {
  // Any base works: `path` is an internal path (sanitizeRedirectPath) and only its parts are read.
  const url = new URL(path, 'http://localhost');
  const query = { ...baseQuery, ...Object.fromEntries(url.searchParams) };
  SELECTION_QUERY_KEYS.forEach((key) => delete query[key]);

  return { path: url.pathname, query, hash: url.hash };
};

// Routes a billing-gated user may still open (the gate itself is in router/index.js).
// /integrations/sp and /integrations/ebay are the OAuth landing pages: bouncing a gated
// user off them to /billing would drop the one-time claim token in the URL and lose the
// grant the user just gave at Amazon. /integrations/shopify/claim is the Shopify handoff
// landing, and its connections page is where the link is managed. /integrations/walmart is the
// Walmart connections page, where an admin whose trial ended must still be able to disconnect.
// /integrations/google/connected is the Google OAuth landing (one-time claim token, same reason).
// /integrations/tiktok/connected is the TikTok one, /integrations/pinterest/connected the Pinterest one,
// /integrations/meta/connected the Meta one, /integrations/bing/connected the Bing one.
export const HARD_GATE_ALLOWED_PATHS = [
  '/billing',
  '/connect-accounts',
  '/user-settings',
  '/settings/connections',
  '/ai/connect',
  '/settings/ai-connections',
  '/integrations/sp',
  '/integrations/ebay',
  '/integrations/shopify',
  '/integrations/walmart',
  '/integrations/google',
  '/integrations/tiktok',
  '/integrations/pinterest',
  '/integrations/meta',
  '/integrations/bing',
];

// Exact match or a sub-path — '/integrations/sp/connected' passes, '/integrations/spx' does not.
export const isHardGateAllowedPath = (path) => {
  const normalizedPath = String(path || '').toLowerCase();

  return HARD_GATE_ALLOWED_PATHS.some(
    (p) => normalizedPath === p || normalizedPath.startsWith(`${p}/`),
  );
};

// Build a redirect to login that preserves the attempted destination as `?to=`, so the user lands
// back there after authenticating. `index` has no meaningful return path, so its `to` is omitted.
export const loginRedirectWithReturn = (to) => {
  const query = { ...to.query };
  // fullPath is already encoded by vue-router. Decoding it here changes nested query
  // values (e.g. %26 becomes a new parameter) and throws on a literal percent sign.
  query.to = to.name !== 'index' ? to.fullPath : undefined;
  return { name: 'login', query };
};

// Resolve the post-auth `?to=` redirect for an already-authenticated user landing on an auth or
// initial page. Single source of truth for ?to= handling (the router guard's two near-identical
// blocks collapsed into this). Returns a vue-router location:
//   - home, when the target path is unsafe (external / protocol-relative / scheme tricks);
//   - the validated target (path + target query merged over the current query, `to` stripped);
//   - the current page with `to` removed, when already on the target.
// `to` is the vue-router route being navigated to; callers gate on `to.query.to` being present.
// Passing `landing` ({ accountStore, ebayAllowed }) makes the unsafe-target case use the
// signed-in landing (seller-only / eBay-only profiles open their own pages, see
// resolveSignedInLanding) and return a Promise; without it the result is synchronous. The landing
// returns the plain home route for a user without User ability (same result as without it), so it
// can't bounce between '/' and 'login' any more than the plain home route does.
export const resolveAuthedToRedirect = (to, { landing = null } = {}) => {
  const returnTo = sanitizeRedirectPath(to.query.to);
  const queryWithoutTo = { ...to.query };
  delete queryWithoutTo.to;

  if (!returnTo) {
    if (landing) {
      return resolveSignedInLanding(landing.accountStore, {
        ebayAllowed: landing.ebayAllowed,
        carryQuery: queryWithoutTo,
      });
    }
    return { ...getHomeRoute(), query: queryWithoutTo };
  }

  // Route queries are already decoded once. Use the same parser as password/SSO login
  // so query values, fragments and tenant-selection stripping stay consistent.
  return returnPathToLocation(returnTo, queryWithoutTo);
};

/**
 * The router guard's decision for the bare `/` entry: an anonymous visitor goes to the login page;
 * a signed-in one to an explicit `?to=` destination first, then the signed-in landing. `/` is
 * never rendered (src/pages/index.vue is only the route record). `accountStore` and `ebayAllowed`
 * are only read for a signed-in user.
 */
export const resolveRootEntry = (to, { isLoggedIn, accountStore, ebayAllowed = false }) => {
  if (!isLoggedIn) return { name: 'login' };
  if (to.query.to) return resolveAuthedToRedirect(to, { landing: { accountStore, ebayAllowed } });
  return resolveSignedInLanding(accountStore, { ebayAllowed, carryQuery: to.query });
};
